import { BRAND } from './brand';
import { LIMITS, MAX_ADMINS } from './constants';
import type { SettingsDoc, SystemSettings } from './models';

/** ברירות המחדל של הגדרות המערכת (כשאין מסמך הגדרות, או ששדה חסר). */
export const SETTINGS_DEFAULTS: SystemSettings = {
  autoArchiveDaysAfter: 1,
  maxWorkersPerContractor: LIMITS.maxWorkers,
  maxAdmins: 10,
  whatsappSignature: BRAND.whatsapp.signature,
  registrationNotice: '',
  personalPageNotice: '',
  publicPaused: false,
  publicPausedMessage: 'ההרשמה סגורה זמנית. נסו שוב מאוחר יותר או פנו למנהל.',
};

export const SETTINGS_LIMITS = {
  autoArchiveDaysAfter: { min: 0, max: 30 },
  maxWorkersPerContractor: { min: 1, max: LIMITS.maxWorkers },
  maxAdmins: { min: 1, max: MAX_ADMINS },
  textMax: 500,
  signatureMax: 120,
} as const;

/** מיזוג מסמך הגדרות (חלקי או חסר) עם ברירות המחדל. ערך לא תקין → ברירת מחדל. */
export function resolveSettings(doc: SettingsDoc<unknown> | null | undefined): SystemSettings {
  const d = doc ?? {};
  const int = (v: unknown, lim: { min: number; max: number }, def: number) =>
    typeof v === 'number' && Number.isInteger(v) && v >= lim.min && v <= lim.max ? v : def;
  const str = (v: unknown, max: number, def: string) => (typeof v === 'string' && v.length <= max ? v : def);
  return {
    autoArchiveDaysAfter: int(d.autoArchiveDaysAfter, SETTINGS_LIMITS.autoArchiveDaysAfter, SETTINGS_DEFAULTS.autoArchiveDaysAfter),
    maxWorkersPerContractor: int(d.maxWorkersPerContractor, SETTINGS_LIMITS.maxWorkersPerContractor, SETTINGS_DEFAULTS.maxWorkersPerContractor),
    maxAdmins: int(d.maxAdmins, SETTINGS_LIMITS.maxAdmins, SETTINGS_DEFAULTS.maxAdmins),
    whatsappSignature: str(d.whatsappSignature, SETTINGS_LIMITS.signatureMax, SETTINGS_DEFAULTS.whatsappSignature),
    registrationNotice: str(d.registrationNotice, SETTINGS_LIMITS.textMax, ''),
    personalPageNotice: str(d.personalPageNotice, SETTINGS_LIMITS.textMax, ''),
    publicPaused: d.publicPaused === true,
    publicPausedMessage: str(d.publicPausedMessage, SETTINGS_LIMITS.textMax, SETTINGS_DEFAULTS.publicPausedMessage) || SETTINGS_DEFAULTS.publicPausedMessage,
  };
}

export type SettingsValidation =
  | { ok: true; value: Partial<SystemSettings> }
  | { ok: false; errors: Partial<Record<keyof SystemSettings, string>> };

/** בדיקת עדכון הגדרות מהמפתח. רק שדות שנשלחו מתעדכנים. */
export function validateSettingsPatch(input: Record<string, unknown>): SettingsValidation {
  const errors: Partial<Record<keyof SystemSettings, string>> = {};
  const value: Partial<SystemSettings> = {};
  const intField = (key: 'autoArchiveDaysAfter' | 'maxWorkersPerContractor' | 'maxAdmins', label: string) => {
    if (!(key in input)) return;
    const v = input[key];
    const lim = SETTINGS_LIMITS[key];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < lim.min || v > lim.max) errors[key] = `${label}: מספר שלם בין ${lim.min} ל-${lim.max}`;
    else value[key] = v;
  };
  const textField = (key: 'whatsappSignature' | 'registrationNotice' | 'personalPageNotice' | 'publicPausedMessage', max: number, required = false) => {
    if (!(key in input)) return;
    const v = input[key];
    if (typeof v !== 'string') return void (errors[key] = 'ערך לא תקין');
    const t = v.replace(/\r\n/g, '\n').trim();
    if (t.length > max) errors[key] = `עד ${max} תווים`;
    else if (required && !t) errors[key] = 'שדה חובה';
    else value[key] = t;
  };
  intField('autoArchiveDaysAfter', 'ימים עד ארכוב');
  intField('maxWorkersPerContractor', 'מקסימום עובדים');
  intField('maxAdmins', 'מספר מנהלים');
  textField('whatsappSignature', SETTINGS_LIMITS.signatureMax, true);
  textField('registrationNotice', SETTINGS_LIMITS.textMax);
  textField('personalPageNotice', SETTINGS_LIMITS.textMax);
  textField('publicPausedMessage', SETTINGS_LIMITS.textMax, true);
  if ('publicPaused' in input) {
    if (typeof input.publicPaused !== 'boolean') errors.publicPaused = 'ערך לא תקין';
    else value.publicPaused = input.publicPaused;
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  if (!Object.keys(value).length) return { ok: false, errors: { autoArchiveDaysAfter: 'לא נשלח שום שינוי' } };
  return { ok: true, value };
}

export const SETTINGS_LABELS: Record<keyof SystemSettings, string> = {
  autoArchiveDaysAfter: 'ימים עד ארכוב אוטומטי',
  maxWorkersPerContractor: 'מקסימום עובדים לקבלן',
  maxAdmins: 'מספר מנהלים מקסימלי',
  whatsappSignature: 'חתימה בהודעות וואטסאפ',
  registrationNotice: 'הודעה במסך ההרשמה',
  personalPageNotice: 'הודעה בדף האישי',
  publicPaused: 'עצירת הרשמות',
  publicPausedMessage: 'הודעה בזמן עצירה',
};
